import { Component, OnInit, EventEmitter, Output, ViewChild, ElementRef, Inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { validateIBAN, validateIBANPortugal } from 'src/app/commons/utils';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UtilService } from 'src/app/services/util.service';
import { InstallationMultiPoint, PreviousInstallation } from '../../new-contract-pymes-multipoint.model';
import { NewContractPymesService } from 'src/app/services/new-contract-pymes.service';
import { NewContractPymesMultipointService } from '../../new-contract-pymes-multipoint.service';
import { GenericResponse, Mobile } from '@interfaces';
import { ToastrService } from 'src/app/shared/ui/toastr/toastr.service';
import * as XLSX from 'xlsx';
import { Select } from '@interfaces';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';
import { AuthService } from 'src/app/auth';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';

@Component({
  selector: 'app-multipoint-type-charge',
  templateUrl: './multipoint-type-charge.component.html',
  styleUrls: ['./multipoint-type-charge.component.scss']
})
export class MultipointTypeChargeComponent implements OnInit {
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public typeChargeForm: FormGroup;
  public roadTypesList: Select[] = [];
  public townNotificationList: Select[] = [];
  private installations: InstallationMultiPoint[] = [];
  private codeProvince: string;
  public feeList: string[] = ['2.0TD', '3.0TD', '6.1TD', '6.2TD', '6.3TD', '6.4TD'];
  public isUserSpanish = true;
  public contactMobileCountryCodeList: Select[];

  @Output() changeLoader: EventEmitter<boolean> = new EventEmitter();
  @Output() data: EventEmitter<PreviousInstallation> = new EventEmitter();

  @ViewChild('inputFile') input: ElementRef<HTMLInputElement>;

  constructor(
    private newContractPymesMultipointService: NewContractPymesMultipointService,
    private newContractPymesService: NewContractPymesService,
    private utilService: UtilService,
    private toastrService: ToastrService,
    private utilValidatorsService: UtilValidatorsService,
    private authService: AuthService,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.authService.getUserData().country === 'ES';
    this.changeLoader.emit(true);
    this.createForm();
    this.getValuesForm();
  }

  private createForm(): void {
    this.typeChargeForm = new FormGroup({
      chargeManual: new FormControl(Validators.required),
      allAddressNotification: new FormControl(false, Validators.required),
      allContactPerson: new FormControl(false, Validators.required),
      allAccountBank: new FormControl(false, Validators.required),
      addressNotifRoadType: new FormControl(null),
      addressNotifName: new FormControl(null, Validators.maxLength(100)),
      addressNotifNumber: new FormControl(null, [Validators.maxLength(8), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]),
      addressNotifGate: new FormControl(null, Validators.maxLength(10)),
      addressNotifStair: new FormControl(null, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]),
      addressNotifFloor: new FormControl(null, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]),
      addressNotifDoor: new FormControl(null, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]),
      addressNotifPostalCode: new FormControl(null),
      addressNotifPostalCodePT1: new FormControl(null),
      addressNotifPostalCodePT2: new FormControl(null),
      addressNotifTown: new FormControl({ value: null, disabled: true }, Validators.maxLength(100)),
      addressNotifProvince: new FormControl({ value: null, disabled: true }, Validators.maxLength(100)),
      contactName: new FormControl(null, Validators.maxLength(50)),
      contactSurname: new FormControl(null, Validators.maxLength(50)),
      contactDocumentNumber: new FormControl('', this.utilValidatorsService.validateCIFNIF),
      contactEmail: new FormControl(null, [Validators.pattern(this.utilValidatorsService.emailPattern), Validators.maxLength(50)]),
      contactMobile: new FormControl(
        { value: '', disabled: !this.isUserSpanish },
        [this.utilValidatorsService.validateMobile, Validators.maxLength(9)]
      ),
      contactMobileCountryCode: new FormControl(''),
      ibanCode: new FormControl(null),
      ibanEntity: new FormControl(null),
      ibanOffice: new FormControl(null),
      ibanDC: new FormControl(null),
      ibanAccount: new FormControl(null)
    });
    this.typeChargeForm.setValidators(
      this.isUserSpanish ? this.validateIBANControls : this.validateIBANControlsPT
    );
    this.initPostalCodePT();
    this.initMobileCountryCodePT();
  }

  private getValuesForm(): void {
    this.newContractPymesMultipointService.getSavedDataMultipoint().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      const mobileAndCountryCode = this.getMobileAndCountryCode(res?.previousInstallation?.contactMobile);
      this.typeChargeForm.patchValue({
        chargeManual: !!res?.previousInstallation?.chargeManual,
        allAddressNotification: !!res?.previousInstallation?.allAddressNotification,
        allContactPerson: !!res?.previousInstallation?.allContactPerson,
        allAccountBank: !!res?.previousInstallation?.allAccountBank,
        addressNotifRoadType: res?.previousInstallation?.addressNotifRoadType || null,
        addressNotifName: res?.previousInstallation?.addressNotifName || null,
        addressNotifNumber: res?.previousInstallation?.addressNotifNumber || null,
        addressNotifGate: res?.previousInstallation?.addressNotifGate || null,
        adressNotifStair: res?.previousInstallation?.addressNotifStair || null,
        addressNotifFloor: res?.previousInstallation?.addressNotifFloor || null,
        addressNotifDoor: res?.previousInstallation?.addressNotifDoor || null,
        addressNotifPostalCode: res?.previousInstallation?.addressNotifPostalCode || null,
        addressNotifPostalCodePT1: this.isUserSpanish
          ? null : res?.previousInstallation?.addressNotifPostalCode?.slice(0, 4) || '',
        addressNotifPostalCodePT2: this.isUserSpanish
          ? null : res?.previousInstallation?.addressNotifPostalCode?.slice(5, 8) || '',
        addressNotifTown: res?.previousInstallation?.addressNotifTown || null,
        addressNotifProvince: res?.previousInstallation?.addressNotifProvince || null,
        contactName: res?.previousInstallation?.contactName || null,
        contactSurname: res?.previousInstallation?.contactSurname || null,
        contactDocumentNumber: res?.previousInstallation?.contactDocumentNumber || '',
        contactEmail: res?.previousInstallation?.contactEmail || null,
        contactMobile: mobileAndCountryCode.mobile || '',
        contactMobileCountryCode: this.isUserSpanish ? null : mobileAndCountryCode.countryCode || '',
        ibanCode: res?.previousInstallation?.ibanCode || null,
        ibanEntity: res?.previousInstallation?.ibanEntity || null,
        ibanOffice: res?.previousInstallation?.ibanOffice || null,
        ibanDC: res?.previousInstallation?.ibanDC || null,
        ibanAccount: res?.previousInstallation?.ibanAccount || null
      });
      this.destroyed$.next();
      this.destroyed$.complete();
      this.getDataDropdowns();
      this.subscribesForm();
    });
  }

  private subscribesForm(): void {
    this.typeChargeForm.valueChanges.subscribe(() => {
      this.emitValues();
    });
    this.typeChargeForm.get('contactMobile').valueChanges.subscribe(
      value => {
        if (value && value !== value.replace(/[^0-9]*/g, '')) {
          this.typeChargeForm.get('contactMobile').setValue(null);
        }
      }
    );
    this.typeChargeForm.get('addressNotifPostalCode').valueChanges.subscribe(
      value => {
        this.typeChargeForm.get('addressNotifTown').setValue(null);
        this.typeChargeForm.get('addressNotifProvince').setValue(null);
        if (value && this.isValidPostalCode()) {
          this.getPostalCode(value);
        }
      }
    );
    this.subscribeFormControlPortugal();
  }

  private emitValues(): void {
    const data: PreviousInstallation = {
      canGoNext: this.checkCanGoNext(),
      installations: this.installations,
      ...this.typeChargeForm.getRawValue()
    };
    if (this.townNotificationList?.length === 1) {
      data.addressNotifTown = this.townNotificationList[0]?.value;
    }
    data.addressNotifProvince = this.codeProvince;
    if (!this.isUserSpanish) {
      data.contactMobile = this.typeChargeForm.get('contactMobileCountryCode').value
        + this.typeChargeForm.get('contactMobile').value;
    }
    this.data.emit(data);
  }

  private checkCanGoNext(): boolean {
    if (this.typeChargeForm.get('chargeManual').value) {
      if (this.typeChargeForm.errors?.invalid_IBAN && this.typeChargeForm.get('allAccountBank').value) {
        return false;
      }
      let canGoNext: boolean;
      for (const key of Object.keys(this.typeChargeForm.controls)) {
        const controlErrors: ValidationErrors = this.typeChargeForm.get(key).errors;
        const contact = this.typeChargeForm.get('allContactPerson').value &&
          (key === 'contactName' || key === 'contactSurname' || key === 'contactDocumentNumber' || key === 'contactEmail' || key === 'contactName' || key === 'contactMobile');
        const address = this.typeChargeForm.get('allAddressNotification').value &&
          (key === 'addressNotifRoadType' || key === 'addressNotifName' || key === 'addressNotifNumber' || key === 'addressNotifGate' ||
            key === 'adressNotifStair' || key === 'addressNotifFloor' || key === 'addressNotifDoor' || key === 'addressNotifPostalCode' ||
            key === 'addressNotifTown' || key === 'addressNotifProvince');
        if (controlErrors && this.typeChargeForm.get(key).value && (contact || address)) {
          canGoNext = false;
          break;
        } else {
          canGoNext = true;
        }
      }
      return canGoNext;
    } else {
      return this.installations.length >= 1;
    }
  }

  private getPostalCode(value: string): void {
    this.changeLoader.emit(true);
    this.newContractPymesService.getCodigoPostal(value).subscribe(
      (response: GenericResponse) => {
        this.postalCodeResponse(response);
        this.changeLoader.emit(false);
      },
      error => {
        this.errorTown(error);
      }
    );
  }

  private postalCodeResponse(response): void {
    if (response.success) {
      this.townNotificationList = this.mapperTown(response.data?.poblaciones);
      if (this.townNotificationList.length <= 1) {
        this.typeChargeForm.get('addressNotifTown').setValue(this.townNotificationList[0]?.label);
        this.typeChargeForm.get('addressNotifTown').disable({ emitEvent: false });
      } else {
        this.typeChargeForm.get('addressNotifTown').enable({ emitEvent: false });
      }
      if (response.data?.nombreProvincia) {
        this.typeChargeForm.get('addressNotifProvince').setValue(response.data.nombreProvincia);
        this.codeProvince = response.data.codigoProvincia;
      }
      this.typeChargeForm.updateValueAndValidity();
    } else {
      this.errorTown(response);
    }
  }

  private mapperTown(response): Select[] {
    return response?.map(value => ({
      label: value.nombrePoblacion,
      value: value.codigoPoblacion
    })) || [];
  }

  private errorTown(error): void {
    this.townNotificationList = [];
    this.changeLoader.emit(false);
    this.utilService.toastErrors(error);
  }

  private validateIBANControls(control: AbstractControl): ValidationErrors | null {
    const iban = control.get('ibanCode').value || null;
    const entity = control.get('ibanEntity').value || null;
    const office = control.get('ibanOffice').value || null;
    const dc = control.get('ibanDC').value || null;
    const account = control.get('ibanAccount').value || null;
    if (!validateIBAN(iban + entity + office + dc + account)) {
      return { invalid_IBAN: true };
    }
    return null;
  }

  private validateIBANControlsPT(control: AbstractControl): ValidationErrors | null {
    const entity = control.get('ibanEntity').value || '';
    const office = control.get('ibanOffice').value || '';
    const dc = control.get('ibanDC').value || '';
    const account = control.get('ibanAccount').value || '';
    if ((entity + office + dc + account) && !validateIBANPortugal(entity, office, dc, account)) {
      return { invalid_IBAN: true };
    }
    return null;
  }

  private getDataDropdowns(): void {
    const services = [this.newContractPymesService.getTipoVia()];
    if (this.typeChargeForm.get('addressNotifPostalCode').value) {
      services.push(this.newContractPymesService.getCodigoPostal(this.typeChargeForm.get('addressNotifPostalCode').value));
    }
    forkJoin(services).subscribe(
      ([viaType, campaign]) => {
        if (viaType.success) {
          this.roadTypesList = viaType.data.map(res => ({ label: res.nombreTipoVia, value: res.nombreTipoVia }));
        } else {
          this.roadTypesList = [];
          this.utilService.toastErrors(viaType);
        }
        if (campaign) {
          this.postalCodeResponse(campaign);
        }
        this.typeChargeForm.updateValueAndValidity();
        this.changeLoader.emit(false);
      },
      error => {
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  public handleDownload(): void {
    this.toastrService.error('COMMONS.DOWNLOADING');
    this.changeLoader.emit(true);
    this.newContractPymesMultipointService.downloadTemplates(true).subscribe(
      response => {
        if (response.type !== 'application/json') {
          const header = response.headers.get('Content-Disposition');
          const fileName = header.split(';')[1].split('=')[1].replace(/\"/g, '');
          const link = document.createElement('a');
          link.download = fileName;
          link.href = URL.createObjectURL(response.body);
          link.click();
          this.toastrService.success('COMMONS.DOWNLOADING_SUCCESS');
        } else {
          const reader = new FileReader();
          reader.addEventListener('loadend', e => {
            this.toastrService.error(JSON.parse(e.target.result as string).Message);
          });
          reader.readAsText(response);
        }
        this.changeLoader.emit(false);
      },
      error => {
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  public handleCharge(file): void {
    this.changeLoader.emit(true);
    const reader = new FileReader();
    reader.onload = () => {
      const workBook = XLSX.read(reader.result, { type: 'binary' });
      const jsonData: Array<Array<any>> = XLSX.utils.sheet_to_json(workBook.Sheets[workBook.SheetNames[0]], {
        header: 1,
        defval: null,
        blankrows: false
      });
      jsonData.splice(0, 1);
      this.installationMapperExcel(jsonData);
      this.checkTowns();
    };
    reader.readAsBinaryString(file);
  }

  private installationMapperExcel(jsonData: any): void {
    this.installations = jsonData.map((installation: Array<any>) => {
      let aux;
      if (this.isUserSpanish) {
        aux = {
          contactMobile: installation[23]?.toString() || null,
          ibanCode: installation[24]?.substring(0, 4) || null,
          ibanEntity: installation[24]?.substring(4, 8) || null,
          ibanOffice: installation[24]?.substring(8, 12) || null,
          ibanDC: installation[24]?.substring(12, 14) || null,
          ibanAccount: installation[24]?.substring(14, 24) || null,
        };
      } else {
        const prefix = installation[23] ? '00' + installation[23]?.toString()?.split('(+')[1]?.replace(')', '') : null;
        const mobile = installation[24]?.toString() || null;
        aux = {
          contactMobile: prefix ? prefix + mobile : mobile,
          ibanCode: installation[25]?.substring(0, 4) || null,
          ibanEntity: installation[25]?.substring(4, 8) || null,
          ibanOffice: installation[25]?.substring(8, 12) || null,
          ibanAccount: installation[25]?.substring(12, 23) || null,
          ibanDC: installation[25]?.substring(23, 25) || null,
        };
      }
      return {
        groupIndex: null,
        name: installation[0]?.substring(0, 40).toString() || null,
        cups: installation[1]?.toString() || '',
        cnae: installation[2]?.toString() || '',
        code: null,
        fee: null,
        feeReal: null,
        geographicZone: null,
        cuPsAcepptedCRM: false,
        codErrorAcepptedCRM: null,
        descriptionErrorAcepptedCRM: null,
        sendedCRM: false,
        registeredCRM: false,
        codErrorRegisteredCRM: null,
        descriptionErrorRegisteredCRM: null,
        estimatedConsumptionPeriod: null,
        contractedPowerPeriod: null,
        energyChannelCommission: null,
        commissionChannelPower: null,
        consumptionEstimated: null,
        cycle: null,
        tension: null,
        period: null,
        addressRoadType: this.capitalizeFirstLetter(installation[3]),
        addressName: installation[4]?.toString() || null,
        addressNumber: installation[5]?.toString() || null,
        addressGate: installation[6]?.toString() || null,
        addressStair: installation[7]?.toString() || null,
        addressFloor: installation[8]?.toString() || null,
        addressDoor: installation[9]?.toString() || null,
        addressPostalCode: installation[10]?.toString() || null,
        addressTown: null,
        addressProvince: null,
        addressNotifRoadType: this.capitalizeFirstLetter(installation[11]),
        addressNotifName: installation[12]?.toString() || null,
        addressNotifNumber: installation[13]?.toString() || null,
        addressNotifGate: installation[14]?.toString() || null,
        addressNotifStair: installation[15]?.toString() || null,
        addressNotifFloor: installation[16]?.toString() || null,
        addressNotifDoor: installation[17]?.toString() || null,
        addressNotifPostalCode: installation[18]?.toString() || null,
        addressNotifTown: null,
        addressNotifProvince: null,
        contactName: installation[19]?.toString() || null,
        contactSurname: installation[20]?.toString() || null,
        contactDocumentNumber: installation[21]?.toString() || '',
        contactEmail: installation[22]?.toString() || null,
        ...aux
      };
    });
  }

  private checkTowns(): void {
    const postalCodes = [];
    this.installations.forEach(value => {
      if (value.addressTown) {
        if (value.addressPostalCode) {
          if (postalCodes.every(value2 => value2 !== value.addressPostalCode)) {
            postalCodes.push(value.addressPostalCode);
          }
        } else {
          value.addressTown = null;
        }
        if (value.addressNotifPostalCode) {
          if (postalCodes.every(value2 => value2 !== value.addressNotifPostalCode)) {
            postalCodes.push(value.addressNotifPostalCode);
          }
        } else {
          value.addressNotifTown = null;
        }
      }
    });
    if (postalCodes.length >= 1) {
      this.getTown(postalCodes);
    } else {
      this.chargeData();
    }
  }

  private getTown(postalCodes: string[]): void {
    const services = postalCodes.map(value => this.newContractPymesService.getCodigoPostal(value));
    forkJoin(services).subscribe(
      results => {
        results.forEach(response => {
          if (response.success) {
            const town = this.mapperTown(response.data?.poblaciones);
            const townInstallation = this.installations.filter(
              value => value.addressPostalCode === response.data.codPostal
            );
            const townNotification = this.installations.filter(
              value => value.addressNotifPostalCode === response.data.codPostal
            );
            if (townInstallation.length >= 1) {
              townInstallation.forEach(res => {
                if (res.addressTown) {
                  if (town.every(val => val.value !== res.addressTown)) {
                    res.addressTown = null;
                  }
                }
              });
            }
            if (townNotification.length >= 1) {
              townNotification.forEach(res => {
                if (res.addressNotifTown) {
                  if (town.every(val => val.value !== res.addressNotifTown)) {
                    res.addressNotifTown = null;
                  }
                }
              });
            }
          }
        });
        this.chargeData();
      }
    );
  }

  private chargeData(): void {
    this.changeLoader.emit(false);
    this.emitValues();
    this.toastrService.error('NEW_CONTRACTS.INSTALLATION_DATA.CHARGED_DATA');
  }

  private capitalizeFirstLetter(value: string): string {
    if (value) {
      let capitalized = value.toString().toLocaleLowerCase();
      capitalized = capitalized.charAt(0).toUpperCase() + capitalized.slice(1);
      return capitalized;
    }
    return null;
  }

  public onPasteIBAN(event: ClipboardEvent): void {
    const pasteIBAN: string = event.clipboardData.getData('text').replace(/\s/g, '');
    if (this.isUserSpanish && pasteIBAN && pasteIBAN.length === 24) {
      this.typeChargeForm.patchValue({
        ibanCode: pasteIBAN.slice(0, 4),
        ibanEntity: pasteIBAN.slice(4, 8),
        ibanOffice: pasteIBAN.slice(8, 12),
        ibanDC: pasteIBAN.slice(12, 14),
        ibanAccount: pasteIBAN.slice(14, 24)
      });
    } else if (!this.isUserSpanish && pasteIBAN && pasteIBAN.length === 25) {
      this.typeChargeForm.patchValue({
        ibanCode: pasteIBAN.slice(0, 4),
        ibanEntity: pasteIBAN.slice(4, 8),
        ibanOffice: pasteIBAN.slice(8, 12),
        ibanAccount: pasteIBAN.slice(12, 23),
        ibanDC: pasteIBAN.slice(23, 25)
      });
    }
  }

  public bankAccountKeyUp(
    event: KeyboardEvent,
    previousInputId: string | null,
    currentInputId: string,
    nextInputId: string | null
  ): void {
    const previousInput = this.getInputElement(previousInputId);
    const currentInput = this.getInputElement(currentInputId);
    const nextInput = this.getInputElement(nextInputId);
    if (!event.ctrlKey && event.key !== 'Control') {
      if (currentInput.selectionStart === currentInput.maxLength && nextInput) {
        nextInput.focus();
        nextInput.setSelectionRange(0, 0);
      } else if (
        (event.key === 'Backspace' || event.key === 'ArrowLeft')
        && currentInput.selectionStart === 0
        && previousInput
      ) {
        previousInput.focus();
      }
    }
  }

  private getInputElement(id: string): HTMLInputElement | null {
    return id ? (document.getElementById(id) as HTMLInputElement) : null;
  }

  private initPostalCodePT(): void {
    if (this.isUserSpanish) {
      this.typeChargeForm.get('addressNotifPostalCode').setValidators([Validators.required, Validators.minLength(5)]);
    } else {
      this.typeChargeForm.get('addressNotifPostalCodePT1').setValidators([Validators.required, Validators.maxLength(4)]);
      this.typeChargeForm.get('addressNotifPostalCodePT2').setValidators([Validators.required, Validators.maxLength(3)]);
      this.typeChargeForm.get('addressNotifPostalCode').setValidators([
        Validators.required,
        Validators.pattern(this.utilValidatorsService.postalCodePTPattern)
      ]);
    }
  }

  private subscribeFormControlPortugal(): void {
    if (!this.isUserSpanish) {
      this.typeChargeForm.get('addressNotifPostalCodePT1').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.typeChargeForm.get('addressNotifPostalCode').setValue(
          value + '-' + this.typeChargeForm.get('addressNotifPostalCodePT2').value
        );
      });
      this.typeChargeForm.get('addressNotifPostalCodePT2').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.typeChargeForm.get('addressNotifPostalCode').setValue(
          this.typeChargeForm.get('addressNotifPostalCodePT1').value + '-' + value
        );
      });

      if (!this.isUserSpanish && this.typeChargeForm.get('contactMobileCountryCode').value) {
        this.mobileCountryCodeChange(this.typeChargeForm.get('contactMobileCountryCode').value);
      }
      this.typeChargeForm.get('contactMobileCountryCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
        value => {
          if (value) {
            this.mobileCountryCodeChange(value);
          }
        }
      );
    }
  }

  private isValidPostalCode(): boolean {
    if (this.isUserSpanish) {
      return this.typeChargeForm.get('addressNotifPostalCode').value?.length > 4;
    } else {
      return this.typeChargeForm.get('addressNotifPostalCodePT1').value?.length === 4 &&
        this.typeChargeForm.get('addressNotifPostalCodePT2').value?.length === 3 &&
        this.typeChargeForm.get('addressNotifPostalCode').value?.length === 8;
    }
  }

  private mobileCountryCodeChange(value: string): void {
    this.typeChargeForm.get('contactMobile').enable();
    if (value === '0034') {
      this.typeChargeForm.get('contactMobile').setValidators(
        [this.utilValidatorsService.validateMobile, Validators.maxLength(9)]
      );
    } else if (value === '00351') {
      this.typeChargeForm.get('contactMobile').setValidators(
        [this.utilValidatorsService.validateMobilePT, Validators.maxLength(9)]
      );
    }
    this.typeChargeForm.get('contactMobile').updateValueAndValidity({ onlySelf: true });
  }

  private getMobileAndCountryCode(value: string | null): Mobile {
    const result: Mobile = { mobile: value };
    if (value) {
      if (!this.isUserSpanish && value?.length > 9) {
        const countryCode = value.slice(0, value.length - 9);
        const validCountryCode = this.contactMobileCountryCodeList.find(code => code.value === countryCode);
        if (validCountryCode) {
          result.countryCode = countryCode;
        }
      }
      result.mobile = value.slice(value.length - 9, value.length);
    }

    return result;
  }

  private initMobileCountryCodePT(): void {
    if (!this.isUserSpanish) {
      this.typeChargeForm.get('contactMobileCountryCode').setValidators(Validators.required);
      this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.contactMobileCountryCodeList = this.utilService.getListCountryMobile();
      });
    }
  }
}
